import { useEffect, useState } from 'react';

import { EntryTypeType, Tabs } from 'chaika-enums';
import { FavoriteType, FrontendDictionaryNames } from 'chaika-frontend-types/frontend-cms';

import { entryService, WellKnownAllowToAssistantEntryTypeIds } from '@patient/services/entry/entryService';
import { CreateParams } from '@hooks/useCreateEntry/types';
import { store } from '@store';
import { OptionType } from '@components/Select';
import { useAssistantDoctors } from '@store/AuthUser/hooks/useAssistantDoctors';
import { entryTypeService } from '@template/services/entryType/entryTypeService';
import { favoriteService } from '@store/AuthUser/services/favoriteService';
import { authUserService } from '@store/AuthUser/services/authUserService';
import { dictionaryService } from '@template/services/dictionary/dictionaryService';
import { CreateEntryOptions } from '@hooks/useCreateEntry/useCreateEntry';

interface Props {
    staticEntryTypeId?: string;
    recordId: number;
    otherParamsForCreation: CreateParams;
    redirectTab?: Tabs;
    onCreate: (params: CreateParams, options: CreateEntryOptions) => Promise<void>;
}

interface Data {
    isDataValid: boolean;
    showUserSelect: boolean;
    entryTypeOptions: OptionType[];
    userOptions: OptionType[];
    selectedUserId?: string;
    selectedEntryTypeId?: string;
    onCancel(): void;
    onConfirm(): Promise<void>;
    onUserSelect(userOptions: OptionType<string>): void;
    onEntryTypeSelect(entryTypeOptions: OptionType<string>): void;
}

export function useCreateEntryModal({
    onCreate,
    recordId,
    staticEntryTypeId,
    redirectTab,
    otherParamsForCreation,
}: Props): Data {
    const { entryTypeList, app, authUser } = store;

    const { getDoctorOptions } = useAssistantDoctors(true);

    const defaultEntryTypeId = authUser.isAssistant ? WellKnownAllowToAssistantEntryTypeIds.AttachedDocument : '';

    const [entryTypeId, setEntryTypeId] = useState<string>(staticEntryTypeId ?? defaultEntryTypeId);
    const [userId, setUserId] = useState<string | undefined>(authUser.id);

    useEffect(() => {
        (async () => {
            await Promise.all([
                entryTypeService.loadActive(),
                favoriteService.load({ types: [FavoriteType.EntryType] }),
                dictionaryService.loadDictionaryIfNotExist(FrontendDictionaryNames.AllowToAssistantEntryType),
                staticEntryTypeId && entryTypeService.loadByIds([staticEntryTypeId]),
            ]);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!staticEntryTypeId) {
            const favoriteList = authUser.favorites.entryTypeFavoriteList;

            if (favoriteList.values.length > 0) {
                const favorite = favoriteList.getMostRecentEntryTypeRecord();

                if (favorite?.data) {
                    setEntryTypeId(favorite.data.entryTypeId);
                }
            }
        }
    }, [
        authUser.favorites.entryTypeFavoriteList,
        authUser.favorites.entryTypeFavoriteList.values,
        authUser.isAssistant,
        staticEntryTypeId,
    ]);

    useEffect(() => {
        const entryType = entryTypeList.get(entryTypeId);

        if (entryType?.type === EntryTypeType.Diagnostics && userId === authUser.id) {
            setUserId(undefined);
        }
    }, [authUser.id, entryTypeId, entryTypeList, entryTypeList.values, userId]);

    const onEntryTypeSelect = ({ value }: OptionType<string>) => {
        if (authUser.isAssistant && !entryService.isAllowToSignByAssistant(value) && userId === authUser.id) {
            setUserId(undefined);
        }
        setEntryTypeId(value);
    };

    const onUserSelect = ({ value }: OptionType<string>) => setUserId(value);

    const assistant: OptionType = { value: authUser.id, label: authUser.fullName };

    const isAvailableForAssistant = entryTypeId && entryService.isAllowToSignByAssistant(entryTypeId);
    const userOptions = isAvailableForAssistant
        ? [assistant, ...getDoctorOptions().filter(({ value }) => value !== assistant.value)]
        : getDoctorOptions();

    const legalEntityId = authUserService.getSelectedOrganizationLegalEntityId();

    const legalEntityEntryTypes = entryTypeList.activeEntryTypes.filter(
        item =>
            !item.legalEntityIds ||
            item.legalEntityIds.length === 0 ||
            (legalEntityId && item.legalEntityIds.includes(legalEntityId)),
    );

    const entryTypeOptions: OptionType[] = legalEntityEntryTypes.map(entryType => ({
        value: entryType.id,
        label: entryType.title,
    }));

    const isDataValid = !!entryTypeId;

    const onConfirm = async () => {
        if (!isDataValid) return;

        app.doOrShowError({
            showLoader: true,
            action: async () => favoriteService.putEntryTypeFavorite(entryTypeId),
        });

        await onCreate(
            { ...otherParamsForCreation, typeId: entryTypeId },
            { recordId, selectedUser: userId, redirectTab },
        );
        app.showPopup(null);
    };

    const onCancel = () => {
        app.showPopup(null);
    };

    return {
        entryTypeOptions,
        userOptions,
        isDataValid,
        selectedEntryTypeId: entryTypeId,
        selectedUserId: userId,
        showUserSelect: !authUser.isDoctor,
        onUserSelect,
        onEntryTypeSelect,
        onConfirm,
        onCancel,
    };
}
